import { useState } from 'react';
import {
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from '@monksflow/monks-ui';
import { CalendarIcon } from 'lucide-react';
import { i18n } from '@/shared/i18n';
import { formatShortDate } from '@/shared/utils/format';
import { resolvePreset, type DateRange, type DatePreset } from './presets';
import type { DateRangePickerProps } from './DateRangePicker.types';

// Getters, not values: this map is built at module evaluation, so a resolved label
// would freeze English at import.
const PRESET_LABELS: Record<Exclude<DatePreset, 'custom'>, string> = {
  get last_7_days() {
    return i18n.t('ui.dateRangePicker.last7Days');
  },
  get last_14_days() {
    return i18n.t('ui.dateRangePicker.last14Days');
  },
  get last_30_days() {
    return i18n.t('ui.dateRangePicker.last30Days');
  },
  get this_month() {
    return i18n.t('ui.dateRangePicker.thisMonth');
  },
  get last_month() {
    return i18n.t('ui.dateRangePicker.lastMonth');
  },
  get flight_to_date() {
    return i18n.t('ui.dateRangePicker.flightToDate');
  },
};

const SIMPLE_PRESETS = [
  'last_7_days',
  'last_14_days',
  'last_30_days',
  'this_month',
  'last_month',
] as const;

function triggerLabel(value: DateRange, preset: DatePreset): string {
  if (preset === 'custom') {
    return `${formatShortDate(value.startDate)} – ${formatShortDate(value.endDate)}`;
  }
  return PRESET_LABELS[preset];
}

/**
 * `presets.ts` is UTC end-to-end and never touches a local accessor. This component
 * deliberately switches convention at the Calendar boundary: `Calendar` (react-day-picker,
 * via date-fns's `getMonth`/`isSameDay`) compares dates in LOCAL time, so handing it a
 * UTC-midnight `Date` opens the wrong month and highlights the wrong day for anyone west of
 * UTC, and reading its selection back with `toISOString()` rolls the date back a day for
 * anyone east of UTC. `toLocalDate`/`toIsoDate` are a matched pair for that boundary only -
 * do not parse an ISO string with the `Date` constructor, do not read one back with
 * `toISOString()`, and do not mix a local conversion with a UTC one.
 *
 * **CAVEAT: The test suite cannot catch a regression here.** `vitest.config.ts` pins
 * `TZ: 'UTC'`, which collapses local and UTC to the same value, so a refactor that
 * reintroduces `T00:00:00Z`/`toISOString()` here would pass the suite and still be wrong for
 * every non-UTC user. This comment is the only guard - keep it and the local calls explicit.
 */
export function toLocalDate(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * A date range with presets, over monks-ui `Calendar` in range mode.
 *
 * `shared/ui/DatePicker` covers a single date only, and this deliberately mirrors its
 * file layout (types / component / index) so the two read alike at a call site. It uses
 * `Popover` rather than `DropdownMenu`: the presets and calendar are floating content
 * anchored to a trigger, not an action menu, so the preset options render as `Button`s
 * inside the popover rather than menu items.
 */
export function DateRangePicker({
  value,
  preset,
  today,
  onChange,
  flightRange = null,
  disabled = false,
}: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" disabled={disabled}>
          <CalendarIcon />
          {triggerLabel(value, preset)}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-auto gap-0 p-0">
        <div className="flex flex-col gap-1 p-2">
          {SIMPLE_PRESETS.map((option) => (
            <Button
              key={option}
              variant="ghost"
              className="justify-start"
              onClick={() => {
                onChange(resolvePreset(option, today), option);
                setIsOpen(false);
              }}
            >
              {PRESET_LABELS[option]}
            </Button>
          ))}
          {/* Offered only when a flight range is actually known. An option that silently
              does nothing is worse than an absent one. */}
          {flightRange && (
            <Button
              variant="ghost"
              className="justify-start"
              onClick={() => {
                onChange(flightRange, 'flight_to_date');
                setIsOpen(false);
              }}
            >
              {PRESET_LABELS.flight_to_date}
            </Button>
          )}
        </div>
        <Separator orientation="vertical" />
        <Calendar
          mode="range"
          defaultMonth={toLocalDate(value.startDate)}
          selected={{
            from: toLocalDate(value.startDate),
            to: toLocalDate(value.endDate),
          }}
          onSelect={(range) => {
            if (!range?.from || !range.to) return;
            onChange(
              {
                startDate: toIsoDate(range.from),
                endDate: toIsoDate(range.to),
              },
              'custom',
            );
            setIsOpen(false);
          }}
          disabled={disabled}
        />
      </PopoverContent>
    </Popover>
  );
}
